import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  fetchZones,
  resolveActiveContext,
  useGroups,
} from "@nju/data-access-portal-dashboard";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import {
  IOfferModeWithFSecure,
  useOfferModeWithFsecureWithoutFallback,
} from "@nju/data-access-portal-offers";
import {
  createOrchestrator,
  IMultiOrderOrchestrator,
  useOrchestrators,
} from "@nju/data-access-portal-orchestrator";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { getEsimQRCode } from "@nju/data-access-portal-sim-card";
import { useUser } from "@nju/data-access-portal-user";
import { portalRoutes } from "@nju/routes";
import { CircleLoader, GeneralErrorNotification, Modal } from "@nju/ui";
import { OverlayContext } from "@nju/ui/src/components/Overlay/context";
import { useIsomorphicLayoutEffect } from "@nju/utils";
import { FSecureDashboardMainPage } from "../FSecureDashboardMainPage";
import { resolveUserId } from "../utils";
import { FSecureOnboardingNotAllowedModal } from "./FSecureOnboardingNotAllowedModal";
import { Ftth } from "./Ftth";
import { Mobile } from "./Mobile";
import { myAccountPageMachine } from "./myAccountPage.machine";
import { events } from "./myAccountPage.machine";
import { OfferTypeSwitcher } from "./OfferTypeSwitcher";
import { Starter } from "./Starter";

interface IMyAccountPageProps {
  handleSetMultiDuoTrioContext: (flow: "multiOrder") => void;
  handleSetSelectedNumber: (userId: string) => void;
  handleSetMode: (mode: IOfferModeWithFSecure) => void;
  isLoading: boolean;
  fSecureModal: OverlayContext;
}

const mobileModes = ["mobile", "postpaid"];

export function MyAccountPage({
  handleSetSelectedNumber,
  handleSetMultiDuoTrioContext,
  handleSetMode,
  isLoading,
  fSecureModal,
}: IMyAccountPageProps) {
  const [shouldRender, setShouldRender] = React.useState(false);
  const router = useRouter();
  const parentOrchestrators = useOrchestrators();
  const memberOrchestrators = useMemberOrchestrator();
  const memberProducts = useMemberProducts();
  const user = useUser();
  const groups = useGroups();
  const selectedProducts = memberProducts;
  const mode = useOfferModeWithFsecureWithoutFallback();
  const params = portalRoutes.mojeKonto.index.input(router.query).unwrap();
  const userId = params?.userId;
  const flow = params?.flow;
  const orderSimNumber = memberOrchestrators?.mobile?.orderSimNumber;
  const simNumber =
    memberProducts?.mobileProducts?.simCard?.characteristic?.sim;
  const isESim =
    memberProducts?.mobileProducts?.simCard?.characteristic?.deliveryType ===
    "ESIM";
  const iccId = orderSimNumber || simNumber;
  const userIdMachine = params?.userId || memberOrchestrators?.mobile?.userId;

  const [current, send] = useMachine(myAccountPageMachine, {
    context: {
      refreshInterval: 5000,
    },
    actions: {
      handleCreateOrderSuccess: () => {
        router.push(portalRoutes.fsecure.wybierzOferte.get());
      },
      handleNoActiveProducts: () => {
        fSecureModal.open();
      },
    },
    services: {
      //TODO: verify if services can be moved to machine
      // was moved outside the machine to keep up to date isESim
      getInitialData: ({ subState }) => {
        return async (callback) => {
          try {
            const esimsResult =
              isESim &&
              iccId &&
              subState !== "Downloaded" &&
              mode &&
              mobileModes.includes(mode)
                ? (
                    await getEsimQRCode({ iccId, userId: userIdMachine })
                  ).unwrap()
                : undefined;

            return callback({
              type: events.SET_DATA,
              subState: esimsResult?.eSim?.subState,
            });
          } catch {
            callback({ type: events.ERROR });
          }
        };
      },
      fetchZones: () => {
        return async (callback) => {
          const result = await fetchZones();

          if (result.isError) {
            return callback({ type: events.ERROR });
          }

          return callback({ type: events.SET_ZONES, zones: result.unwrap() });
        };
      },
      createOrder: () => {
        return async (callback) => {
          const result = await createOrchestrator("NJU_FSECURE_ONBOARDING", {});

          if (result.isError) {
            if (result.err.code === "NO_ACTIVE_PRODUCTS") {
              return callback({ type: events.NO_ACTIVE_PRODUCTS });
            }
            return callback({ type: events.CREATE_ORDER_ERROR });
          }

          return callback({ type: events.CREATE_ORDER_SUCCESS });
        };
      },
    },
  });

  useIsomorphicLayoutEffect(() => {
    setShouldRender(false);

    const currentOfferCategory: IMultiOrderOrchestrator["offerCategory"] =
      mode === "postpaid" ? "MOBILE_POSTPAID" : "MOBILE";
    const mobileOrchestrator =
      (mode === "mobile" &&
        memberOrchestrators?.mobile &&
        !memberOrchestrators.mobile.offerCategory) ||
      memberOrchestrators?.mobile?.offerCategory === currentOfferCategory
        ? memberOrchestrators?.mobile
        : undefined;
    const multiOrderOrchestrator =
      (mode === "mobile" &&
        parentOrchestrators?.mobile &&
        !parentOrchestrators.mobile.offerCategory) ||
      parentOrchestrators?.multiOrder?.offerCategory === currentOfferCategory
        ? parentOrchestrators?.multiOrder
        : undefined;

    const resolvedUserId = resolveUserId({
      mobileOrchestrator,
      multiOrderOrchestrator,
      adminId: user.id,
      userIdFromQueryParams: userId,
      flow,
      members: groups?.[0]?.members.filter((member) => {
        return (
          member.offerCategory === currentOfferCategory ||
          (mode === "mobile" && !member.offerCategory)
        );
      }),
    });

    if (
      !resolvedUserId &&
      params?.mode &&
      mobileModes.includes(params.mode) &&
      !params.flow &&
      parentOrchestrators?.multiOrder &&
      parentOrchestrators?.multiOrder.state !== "START"
    ) {
      router.replace(
        portalRoutes.mojeKonto.index.get({
          mode: params.mode,
          flow: "multiOrder",
        }),
      );
      handleSetMultiDuoTrioContext("multiOrder");
    }

    if (
      params?.mode &&
      mobileModes.includes(params.mode) &&
      resolvedUserId &&
      (!params?.userId || params?.userId !== resolvedUserId)
    ) {
      router.replace(
        portalRoutes.mojeKonto.index.get({
          mode: params.mode,
          userId: resolvedUserId,
        }),
      );
      handleSetSelectedNumber(resolvedUserId);
    }

    if (
      mode === "fsecure" &&
      (!parentOrchestrators?.fsecure ||
        parentOrchestrators?.fsecure?.state === "START")
    ) {
      router.push(portalRoutes.mojeKonto.index.get({ mode: "ftth" }));
    }

    if (
      (mode && mobileModes.includes(mode)) ||
      mode === "ftth" ||
      mode === "fsecure"
    ) {
      setShouldRender(true);
      window.sessionStorage.setItem("mode", mode);
    }
  }, [memberOrchestrators, router, groups, parentOrchestrators]);

  const accountPageContext: IOfferModeWithFSecure | undefined =
    resolveActiveContext({
      ftth: parentOrchestrators?.ftth,
      fsecure: parentOrchestrators?.fsecure,
      mobile: memberOrchestrators?.mobile || parentOrchestrators?.mobile,
      multiOrder: parentOrchestrators?.multiOrder,
      mobileProducts: selectedProducts?.mobileProducts,
      ftthProducts: selectedProducts?.ftthProducts,
      mode,
    });

  const handleMissingFSecure = () => {
    send({ type: events.CREATE_ORDER });
  };

  if (!shouldRender || isLoading || current.matches("loading")) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircleLoader />
      </div>
    );
  }

  if (current.matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return router.reload();
        }}
      />
    );
  }

  return (
    <>
      {fSecureModal.isOpened && (
        <Modal onClose={fSecureModal.close} title="Jeszcze chwila">
          <FSecureOnboardingNotAllowedModal onClose={fSecureModal.close} />
        </Modal>
      )}
      {accountPageContext && (
        <OfferTypeSwitcher
          context={accountPageContext}
          handleMissingFSecure={handleMissingFSecure}
          handleSetMode={handleSetMode}
          isLoading={current.matches("createOrder")}
        />
      )}
      {accountPageContext === "ftth" && (
        <Ftth ftthOrchestrator={parentOrchestrators?.ftth} />
      )}
      {accountPageContext && mobileModes.includes(accountPageContext) && (
        <Mobile
          handleSetMultiDuoTrioContext={handleSetMultiDuoTrioContext}
          handleSetSelectedNumber={handleSetSelectedNumber}
          isLoading={isLoading}
          multiOrderOrchestrator={parentOrchestrators?.multiOrder}
          selectedMobileProducts={selectedProducts?.mobileProducts}
          selectedOrchestrators={memberOrchestrators}
          subState={current.context.subState}
          zones={current.context.zones}
        />
      )}
      {accountPageContext === "fsecure" && <FSecureDashboardMainPage />}
      {!accountPageContext && (
        <div className="flex flex-col gap-4">
          <Starter />
        </div>
      )}
    </>
  );
}
